import { Link } from "react-router-dom";
import { PawPrint, Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { business, nav, telLink, assets } from "@/data/site";
import { EstimateButton } from "@/components/CTAButtons";

export default function Footer() {
  const year = new Date().getFullYear();
  const links = [...nav, { label: "Privacy Policy", to: "/privacy-policy" }];

  return (
    <footer className="relative overflow-hidden bg-forest text-cream">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true" />
      <div className="container-x relative py-16">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <img
              src={assets.logo}
              alt="Benji's Hauling & Junk Removal logo"
              width="220" height="220"
              className="h-28 w-auto"
            />
            <p className="mt-4 flex items-center gap-2 text-cream/80">
              <PawPrint className="h-4 w-4 text-gold" strokeWidth={2.2} />
              <span className="font-heading text-lg font-semibold">{business.tagline}</span>
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-cream/70">
              Friendly, reliable, full-service junk removal and property cleanouts with straightforward
              pricing starting at ${business.startingPrice}. We load it, haul it, and sweep up before we go.
            </p>
            <div className="mt-6">
              <EstimateButton label="Get a Free Estimate" variant="primary" testid="footer-estimate" />
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-gold">Explore</h4>
            <ul className="mt-4 space-y-2.5">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-cream/75 transition-colors hover:text-gold">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-gold">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm text-cream/75">
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <a href={telLink} className="hover:text-gold" data-testid="footer-phone">{business.phoneDisplay}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span data-testid="footer-email">{business.email}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span data-testid="footer-area">{business.serviceArea}</span>
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              <a href={business.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center rounded-md border border-cream/20 text-cream transition-colors hover:border-gold hover:text-gold">
                <Instagram className="h-5 w-5" />
              </a>
              <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center rounded-md border border-cream/20 text-cream transition-colors hover:border-gold hover:text-gold">
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-cream/15 pt-6 text-xs text-cream/55 sm:flex-row">
          <p>© {year} Benji's Hauling &amp; Junk Removal. All rights reserved.</p>
          <p className="flex items-center gap-1.5">
            Made with loyalty <PawPrint className="h-3.5 w-3.5 text-gold" strokeWidth={2.4} />
          </p>
        </div>
      </div>
    </footer>
  );
}
